import React, { FC, ReactNode, useEffect, useState } from 'react' // we need this to make JSX compile
import Icon from './Icon'
import Button from './forms/Button'
import { ColorType } from 'common/types/color-type'
import useWindowSize from './util/useWindowSize'
import Strings from 'project/localisation'
import classnames from 'classnames'
import classNames from 'classnames'
import Heading from './Heading'

export type AccordionItemType = {
  item: {
    title: string
    body: ReactNode
    titleAppend?: string
  }
  headerSize?: 1 | 2 | 3 | 4 | 5 | 6 | undefined
  className?: string
  titleForItem?: string
  appendColor?: ColorType
  onClick: () => void
  active: boolean
  chevronColour?: string
  bodySize?: string
}

export type AccordionType = {
  chevronColour?: string
  appendColor?: ColorType
  headerSize?: 1 | 2 | 3 | 4 | 5 | 6
  itemClassName?: string
  items: AccordionItemType['item'][]
  expandAllButton?: boolean
  titleForItems?: string
  expandAllButtonMobileOnly?: boolean
  expandAllButtonDesktopOnly?: boolean
  activeItem?: number | null
  bodySize?: string
}
let useCollapse = () => ({
  getCollapseProps: (x?: any) => ({}),
  getToggleProps: (x?: any) => ({}),
})
const prom = import('react-collapsed')
let resolved = false

const AccordionItem: FC<AccordionItemType> = ({
  active,
  appendColor,
  bodySize = 'pt-4',
  chevronColour,
  className = 'py-3 py-lg-3',
  headerSize = 3,
  item,
  onClick,
  titleForItem,
}) => {
  const [ready, setReady] = useState(resolved)
  const windowSize = useWindowSize()

  useEffect(() => {
    if (!resolved) {
      prom.then((res) => {
        resolved = true
        useCollapse = res.default
        setReady(true)
      })
    } else {
      setReady(true)
    }
  }, [])
  // @ts-ignore
  const { getCollapseProps, getToggleProps, isExpanded, setExpanded } =
    useCollapse()

  useEffect(() => {
    if (ready && isExpanded !== active) {
      setExpanded(active)
    }
  }, [active, isExpanded, ready, setExpanded])

  return (
    <>
      <div className={classNames('collapsible', className)}>
        <div
          className='collapsible__header d-flex'
          {...getToggleProps({ onClick })}
        >
          <Heading
            size={headerSize || 3}
            className='fs-fluid-20-24 text-semibold d-inline pe-sm-0 pe-sm-8 pe-8'
          >
            {item.title}{' '}
            {item.titleAppend && (
              <span className={`color-${appendColor}`}>{item.titleAppend}</span>
            )}
          </Heading>
          <span className='ms-auto d-inline'>
            {isExpanded ? (
              <Icon
                fill={chevronColour}
                name='chevron-up'
                width={24}
                height={24}
              />
            ) : (
              <Icon
                fill={chevronColour}
                name='chevron-down'
                width={24}
                height={24}
              />
            )}
          </span>
        </div>
        <div {...getCollapseProps()}>
          {ready && (
            <div className={classnames('accordion-items-container', bodySize)}>
              {titleForItem && (
                <p className={`text-semibold color-${appendColor}`}>
                  {titleForItem}
                </p>
              )}
              {active && item.body}
            </div>
          )}
        </div>
      </div>
    </>
  )
}

const Accordion: FC<AccordionType> = ({
  activeItem = null,
  appendColor,
  bodySize = 'pt-4',
  chevronColour,
  expandAllButton,
  expandAllButtonDesktopOnly = false,
  expandAllButtonMobileOnly = false,
  headerSize = 3,
  itemClassName = 'py-3 py-lg-3',
  items,
  titleForItems,
}) => {
  const [active, setActive] = useState<number | null>(activeItem)
  const windowSize = useWindowSize()

  return (
    <>
      {items.map((item, i) => {
        return (
          <AccordionItem
            headerSize={headerSize}
            className={itemClassName}
            chevronColour={chevronColour}
            key={i}
            appendColor={appendColor}
            titleForItem={titleForItems}
            item={item}
            onClick={() => {
              setActive(i === active ? null : i)
            }}
            active={active === i}
            bodySize={bodySize}
          />
        )
      })}
    </>
  )
}

export default Accordion
